import { useState, useEffect } from 'react';
import * as MdIcons from 'react-icons/md';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import "../../index.css";
import { useAuthProvider } from "../../Providers/AuthProvider"
import { useEndpointProvider } from '../../Providers/EndpointProvider';
import { FRONTEND_LINKS } from '../../Constants/FrontendLinks';
import AccountSettingsModal from './AccountSettingsModal';
import { useModalProvider } from '../../Providers/ModalProvider';
import { useUploadIntoQueueProvider } from '../../Providers/UploadIntoQueueProvider';
import { useUploadQueueProvider } from '../../Providers/UploadQueueProvider';
import { MobergButton, MobergButtonShape } from "../MobergButton/MobergButton";
import { MobergTheme } from '../MobergThemes/MobergColors';
import { MobergIconSize } from "../../Components/MobergIcon/MobergIcon";
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import { useBackendLinksProvider } from '../../Providers/BackendLinksProvider';
import { MobergFontFamily } from '../MobergFont/MobergFont';
import { Deployment, useEnvironmentVariablesProvider } from '../../Providers/EnvironmentVariablesProvider';
import { useWorkspacesProvider } from '../../Providers/WorkspacesProvider';
import WorkspaceDisplay from '../WorkspaceDisplay/WorkspaceDisplay';
import logoIcon from '../../assets/images/icon.png';
import darkLogoIcon from '../../assets/images/dark-icon.png';
import { useMobergTheme } from '../../Providers/MobergThemeProvider';

// Styled Components
const HeaderContainer = styled.div`
    display: ${props => props.enabled ? "flex" : "none"};
    justify-content: space-between;
    align-items: center;
    padding: 8px 16px;
    background: var(--color-surface-panel);
    border-bottom: 1px solid var(--color-border-light);
    height: 60px;
    position: relative;
    z-index: 100;
`;

const LeftSection = styled.div`
    display: flex;
    align-items: center;
    margin-left: 16px;
    gap: 16px;
`;

const BrandTitle = styled.h1`
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    font-size: 22px;
    color: var(--text-color);
    letter-spacing: 0.04em;
    margin: 0;
    line-height: 1;
`;

const RightSection = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const Divider = styled.div`
    height: 32px;
    width: 1.5px;
    background: var(--color-border-default);
    margin: 0 12px;
    border-radius: 1px;
`;

const UserSection = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const EmailDisplayContainer = styled.div`
    font-family: ${MobergFontFamily.HEADER};
    font-size: 16px;
    font-weight: 700;
    color: var(--text-color);
    padding: 8px;
    min-width: 180px;
    min-height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const EmailSkeleton = styled.div`
    width: 180px;
    height: 24px;
    background: var(--color-hover-neutral);
    border-radius: 4px;
`;

const AccountButton = styled(MobergButton)`
    color: var(--text-secondary-color);

    &:hover {
        color: var(--text-color);
    }
`;

const BottomDivider = styled.div`
    margin-left: 132px;
    width: calc(100% - 132px);
    height: 1px;
    background: var(--color-border-default);
    margin-top: 0;
    margin-bottom: 8px;
`;

const LogoButton = styled.button`
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    transition: opacity 0.2s ease;
    
    &:hover {
        opacity: 0.8;
    }
    
    &:active {
        opacity: 0.6;
    }
`;

function PageHeader(props) {
    const authProvider = useAuthProvider()
    const endpointProvider = useEndpointProvider()
    const { createModal } = useModalProvider()
    const uploadIntoQueueProvider = useUploadIntoQueueProvider()
    const uploadQueueProvider = useUploadQueueProvider()
    const { LINKS } = useBackendLinksProvider()
    const { deployment } = useEnvironmentVariablesProvider()
    const isClinical = deployment === Deployment.CLINICAL
    const { colorScheme, setColorScheme } = useMobergTheme()
    const isDark = colorScheme === "dark"

    const location = useLocation()

    async function cancelUpload() {
        if (uploadIntoQueueProvider.uploadID) {
            let body = {
                upload_id: uploadIntoQueueProvider.uploadID,
            }
            return endpointProvider.post(LINKS.DATA.UPLOAD.CANCEL_UPLOAD, body)
        }
    }

    function logout() {
        uploadIntoQueueProvider.setOpenQueue(false)
        uploadIntoQueueProvider.undoExistingValidation(uploadIntoQueueProvider.folderID)
        uploadQueueProvider.setUploadInProgress(false)
        props.setSidebar(false)
        props.setNavPage(false)
        authProvider.logout()
        navigate(FRONTEND_LINKS.MAIN)
    }

    const navigate = useNavigate()
    const handleLogout = (e) => {
        // This means there is an ongoing upload. We need to confirm before we cancel the upload
        if (uploadQueueProvider.uploadInProgress) {
            createModal(<ConfirmationModal
                title={"Remove upload?"}
                description={`Are you sure you want to log out? Logging out will cancel your current upload and remove all uploads from queue.`}
                confirmButton={{ text: "Yes, log out", theme: MobergTheme.RED, onClick: cancelUpload }}
                cancelButton={{ text: "No, continue upload" }}
                afterClose={() => logout()}
            />)
        } else {
            logout()
        }
    }

    function renderAccountSettingsModal() {
        createModal(<AccountSettingsModal
            escClose={false}
            clickOutsideClose={false}
        />)
    }

    const handleLogoClick = () => {
        if (isClinical) {
            navigate(FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.VISUALIZE.MAIN, { 
                state: { admission_status: "admitted" } 
            });
        } else {
            navigate(FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.VISUALIZE.MAIN);
        }
    }

    const [enablePageHeader, setEnablePageHeader] = useState(true)

    useEffect(() => {
        if (authProvider.userIsLoggedIn && location.pathname === '/inactive') {
            setEnablePageHeader(false)
        } else {
            setEnablePageHeader(true)
        }

        if (!authProvider.userIsLoggedIn) {
            setEnablePageHeader(false)
        }
    }, [authProvider.userIsLoggedIn])

    return (
        <>
            <HeaderContainer enabled={enablePageHeader}>
                <LeftSection>
                    <LogoButton onClick={handleLogoClick} aria-label="Go to homepage">
                        <img src={isDark ? darkLogoIcon : logoIcon} alt="Logo" style={isDark ? { height: 32, width: "auto", marginRight: 12 } : { width: 42, height: 42, marginRight: 12 }} />
                    </LogoButton>
                    <BrandTitle>
                        Moberg {isClinical ? "Clinical Platform" : "Cloud Platform"}
                    </BrandTitle>
                </LeftSection>
                
                <RightSection>
                    <WorkspaceDisplay />
                    <Divider />

                    <UserSection>
                        {authProvider.currentUser?.email ? (
                            <EmailDisplayContainer>
                                {authProvider.currentUser.email}
                            </EmailDisplayContainer>
                        ) : (
                            <EmailSkeleton />
                        )}
                        {!isClinical && (
                            <AccountButton
                                onClick={renderAccountSettingsModal}
                                shape={MobergButtonShape.SQUARE}
                            >
                                <MdIcons.MdAccountCircle size={MobergIconSize.LARGE} color={"var(--color-accent-interactive)"} />
                            </AccountButton>
                        )}
                    </UserSection>

                </RightSection>
            </HeaderContainer>
            {/* <BottomDivider /> */}
        </>
    )
};

export default PageHeader;